import { normalizeApiArrayResponse } from "../api/normalizeApiResponse.js";

export interface CmtsCalendarItem extends Record<string, unknown> {
  readonly id: string;
  readonly title: string;
  readonly start?: string | undefined;
  readonly end?: string | undefined;
  readonly dueDate?: string | undefined;
  readonly status?: string | undefined;
  readonly type?: string | undefined;
  readonly eventType?: string | undefined;
  readonly ownerName?: string | undefined;
}

export interface CmtsSchedulerItem extends Record<string, unknown> {
  readonly id: string;
  readonly title: string;
  readonly status?: string | undefined;
  readonly type?: string | undefined;
  readonly nextRun?: string | undefined;
}

export function normalizeCalendarTask(input: unknown): CmtsCalendarItem | undefined {
  if (!isRecord(input)) return undefined;
  const id = readFirstString(input, ["id", "eventId", "calendarEventId", "calendarProfileId", "reminderId"]);
  if (!id) return undefined;
  const title = readFirstString(input, ["title", "name", "subject", "summary", "calendarName", "code"]) ?? "Calendar event";
  const start = readFirstString(input, ["start", "startAt", "startDate", "startTime", "startDateTime", "from"]);
  const end = readFirstString(input, ["end", "endAt", "endDate", "endTime", "endDateTime", "to"]);
  const dueDate = readFirstString(input, ["dueDate", "dueAt", "nextRun", "scheduledAt", "startDateTime"]);
  const eventType = readFirstString(input, ["eventType", "type", "calendarType", "ruleType"]);
  const status = readFirstString(input, ["status", "state"]);
  const ownerName = readFirstString(input, ["ownerName", "owner", "assignedTo", "ownerUserName"]);
  return {
    ...input,
    id,
    title,
    ...(start ? { start } : {}),
    ...(end ? { end } : {}),
    ...(dueDate ? { dueDate } : {}),
    ...(eventType ? { eventType, type: eventType } : {}),
    ...(status ? { status } : {}),
    ...(ownerName ? { ownerName } : {}),
  };
}

export function normalizeCalendarTasksResponse(input: unknown): CmtsCalendarItem[] {
  return normalizeApiArrayResponse<unknown>(input, { allowSingleObject: true })
    .map(normalizeCalendarTask)
    .filter((row): row is CmtsCalendarItem => row !== undefined);
}

export function normalizeSchedulerItem(input: unknown): CmtsSchedulerItem | undefined {
  if (!isRecord(input)) return undefined;
  const id = readFirstString(input, ["id", "jobId", "scheduledJobId", "scheduledJobDefinitionId"]);
  if (!id) return undefined;
  const title = readFirstString(input, ["title", "jobName", "name", "summary", "jobCode", "code"]) ?? "Scheduled job";
  const nextRun = readFirstString(input, ["nextRun", "nextRunAt", "scheduledAt", "startedAt"]);
  const status = readFirstString(input, ["status", "state"]);
  const type = readFirstString(input, ["type", "jobType"]);
  return {
    ...input,
    id,
    title,
    ...(nextRun ? { nextRun } : {}),
    ...(status ? { status } : {}),
    ...(type ? { type } : {}),
  };
}

export function normalizeSchedulerResponse(input: unknown): CmtsSchedulerItem[] {
  return normalizeApiArrayResponse<unknown>(input, { allowSingleObject: true })
    .map(normalizeSchedulerItem)
    .filter((row): row is CmtsSchedulerItem => row !== undefined);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function readFirstString(row: Record<string, unknown>, keys: readonly string[]): string | undefined {
  for (const key of keys) {
    const value = row[key];
    if (typeof value === "string" && value.trim()) return value.trim();
    if (typeof value === "number" && Number.isFinite(value)) return String(value);
  }
  return undefined;
}
